import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Trash2, Eye } from 'lucide-react';
import { offlineDB } from '../offline/db';
import { toast } from 'sonner';

/**
 * Visualiseur des conflits de synchronisation : actions hors-ligne refusées par le serveur
 * (HTTP 409/412). L'utilisateur peut inspecter le payload local et soit le supprimer,
 * soit le re-soumettre manuellement.
 */
export const ConflictResolverCard = () => {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    const list = await offlineDB.conflict_log.toArray();
    setConflicts(list.reverse());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    await offlineDB.conflict_log.delete(id);
    toast.success('Conflit ignoré');
    load();
  };

  const clearAll = async () => {
    await offlineDB.conflict_log.clear();
    toast.success('Tous les conflits effacés');
    load();
  };

  if (loading) return null;
  if (conflicts.length === 0) {
    return (
      <Card className="bg-zinc-900 border-zinc-800" data-testid="conflict-resolver-card-empty">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-zinc-500" />
            Conflits de synchronisation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-500">Aucun conflit à résoudre.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-amber-500/30" data-testid="conflict-resolver-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Conflits de synchronisation
            <span className="text-xs text-zinc-500 font-normal ml-2">{conflicts.length} en attente</span>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={clearAll} className="text-zinc-500 hover:text-red-400">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Tout effacer
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {conflicts.map((c) => (
          <div key={c.id} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-xs" data-testid={`conflict-item-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-amber-300">
                {c.entity} · {c.action} · {(c.entity_id || '').toString().slice(0, 8)}
              </span>
              <span className="text-[10px] text-zinc-500">{new Date(c.created_at).toLocaleString('fr-FR')}</span>
            </div>
            <p className="text-zinc-400 italic mb-1">{c.server_error || 'Conflit inconnu'}</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      className="h-6 text-[10px] text-zinc-400 hover:text-white">
                <Eye className="w-3 h-3 mr-1" /> {expanded === c.id ? 'Masquer' : 'Voir le payload'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => dismiss(c.id)}
                      className="h-6 text-[10px] text-zinc-500 hover:text-red-400">
                <Trash2 className="w-3 h-3 mr-1" /> Ignorer
              </Button>
            </div>
            {expanded === c.id && (
              <pre className="mt-2 p-2 rounded bg-black/40 text-[10px] text-zinc-400 overflow-auto max-h-40">
                {JSON.stringify(c.payload, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default ConflictResolverCard;
